import * as React from 'react';

import { cn, mergeRefs } from '../lib/utils';
export interface InputProps extends React.ComponentProps<'input'> {
  retainFocus?: boolean;
  dataTestId?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, retainFocus, onBlur, onKeyDown, dataTestId, ...props },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = mergeRefs(ref, inputRef);

    React.useEffect(() => {
      if (retainFocus && inputRef.current) {
        inputRef.current.focus();
      }
    }, [retainFocus]);

    // if blur was caused programmatically, refocus on input
    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (retainFocus) {
          e.preventDefault();
          inputRef.current?.focus();
        }
        onBlur?.(e);
      },
      [retainFocus, onBlur],
    );

    // escape and click outside are allowed ways of blurring
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (retainFocus && e.key === 'Escape') {
          inputRef.current?.blur();
        }
        onKeyDown?.(e);
      },
      [retainFocus, onKeyDown],
    );

    return (
      <input
        type={type}
        className={cn(
          'vai-flex vai-h-10 vai-w-full vai-rounded-md vai-border vai-border-input vai-border-solid vai-bg-background vai-px-3 vai-py-2 vai-text-base vai-ring-offset-background file:vai-border-0 file:vai-bg-transparent file:vai-text-sm file:vai-font-medium file:vai-text-foreground placeholder:vai-text-muted-foreground focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2  disabled:vai-cursor-not-allowed disabled:vai-opacity-50 md:vai-text-sm',
          className,
        )}
        ref={combinedRef}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...props}
        data-testid={dataTestId}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
